<template>
  <v-card class="elevation-0">
    <v-card-title class="font-weight-normal">{{ $t('admin.title') }}</v-card-title>
    <v-card-subtitle class="font-weight-light">{{ $t('admin.subtitle') }}</v-card-subtitle>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab>{{ $t('admin.tabs.updateEmail') }}</v-tab>
      <v-tab>{{ $t('admin.tabs.createUser') }}</v-tab>
      <v-tab>{{ $t('admin.tabs.deleteUser') }}</v-tab>
      <v-tab>Update Integration Name</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- Update User Email Tab -->
      <v-tab-item>
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedUser"
                  :items="users"
                  item-text="username"
                  item-value="username"
                  :label="$t('user.label')"
                  :placeholder="$t('user.placeholder')"
                  outlined
                  dense
                  class="mb-3"
                  :loading="loadingUsers"
                  @input="onUserSelected"
                ></v-select>
                <v-text-field
                  v-model="emailAddress"
                  :placeholder="$t('email.placeholder')"
                  :label="$t('email.title')"
                  :error-messages="emailError"
                  outlined
                  dense
                  class="mb-3"
                  @input="validateEmail"
                ></v-text-field>
                <v-btn 
                  color="primary" 
                  :disabled="!emailValid || !selectedUser"
                  @click="handleUpdateEmail"
                >
                  {{ $t('action.update') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Create User Tab -->
      <v-tab-item>
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-form ref="createUserForm" v-model="createUserFormValid">
                  <v-text-field
                    v-model="newUser.username"
                    :placeholder="$t('createUser.usernamePlaceholder')"
                    :label="$t('createUser.username')"
                    :rules="[v => !!v || $t('createUser.errors.usernameRequired')]"
                    outlined
                    dense
                    class="mb-3"
                  ></v-text-field>
                  <v-text-field
                    v-model="newUser.password"
                    :placeholder="$t('createUser.passwordPlaceholder')"
                    :label="$t('createUser.password')"
                    :rules="passwordRules"
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    outlined
                    dense
                    class="mb-3"
                    @click:append="showPassword = !showPassword"
                  ></v-text-field>
                  <v-text-field
                    v-model="newUser.email"
                    :placeholder="$t('createUser.emailPlaceholder')"
                    :label="$t('createUser.email')"
                    :rules="[
                      v => !v || emailRegex.test(v) || $t('createUser.errors.invalidEmail')
                    ]"
                    outlined
                    dense
                    class="mb-3"
                  ></v-text-field>
                  <v-select
                    v-model="selectedTenantForUser"
                    :items="availableTenants"
                    :item-text="getTenantDisplayName"
                    :item-value="getTenantValue"
                    :label="$t('createUser.selectTenant')"
                    :placeholder="$t('createUser.selectTenantPlaceholder')"
                    :rules="newUser.newTenantId ? [] : [v => !!v || $t('createUser.errors.tenantRequired')]"
                    :disabled="!!newUser.newTenantId"
                    outlined
                    dense
                    class="mb-3"
                    :loading="loadingTenants"
                  ></v-select>
                  <v-text-field
                    v-model="newUser.newTenantId"
                    :placeholder="$t('createUser.newTenantIdPlaceholder')"
                    :label="$t('createUser.newTenantId')"
                    :hint="$t('createUser.newTenantIdHint')"
                    persistent-hint
                    outlined
                    dense
                    class="mb-3"
                    @input="onNewTenantIdInput"
                  ></v-text-field>
                  <v-btn 
                    color="primary" 
                    :disabled="!createUserFormValid"
                    :loading="creatingUser"
                    @click="handleCreateUser"
                  >
                    {{ $t('createUser.createButton') }}
                  </v-btn>
                </v-form>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Delete User Tab -->
      <v-tab-item>
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="userToDelete"
                  :items="users"
                  item-text="username"
                  item-value="username"
                  :label="$t('deleteUser.selectUser')"
                  :placeholder="$t('deleteUser.selectPlaceholder')"
                  outlined
                  dense
                  class="mb-3"
                  :loading="loadingUsers"
                ></v-select>
                <v-btn 
                  color="error" 
                  :disabled="!userToDelete"
                  @click="showDeleteConfirmation = true"
                >
                  {{ $t('deleteUser.deleteButton') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Update Integration Name Tab -->
      <v-tab-item>
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedTenantForIntegration"
                  :items="availableTenants"
                  :item-text="getTenantDisplayName"
                  :item-value="getTenantValue"
                  label="Select Tenant ID"
                  placeholder="Select a tenant"
                  outlined
                  dense
                  class="mb-3"
                  :loading="loadingTenants"
                  @input="onTenantSelectedForIntegration"
                ></v-select>
                <v-select
                  v-model="selectedIntegration"
                  :items="integrations"
                  item-text="name"
                  item-value="name"
                  label="Select Integration Name"
                  placeholder="Select an integration"
                  outlined
                  dense
                  class="mb-3"
                  :loading="loadingIntegrations"
                  :disabled="loadingTenantIntegration"
                ></v-select>
                <v-btn 
                  color="primary" 
                  :disabled="!selectedTenantForIntegration || !selectedIntegration"
                  @click="handleUpdateIntegrationName"
                >
                  Update
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteConfirmation" max-width="400">
      <v-card>
        <v-card-title class="headline">{{ $t('deleteUser.confirmTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('deleteUser.confirmMessage') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteConfirmation = false">
            {{ $t('action.cancel') }}
          </v-btn>
          <v-btn color="error" text @click="handleDeleteUser">
            {{ $t('action.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'Admin',
  data() {
    return {
      tab: 0,
      // Update Email tab
      selectedUser: '',
      users: [],
      loadingUsers: false,
      emailAddress: '',
      emailError: '',
      emailValid: false,
      // Create User tab
      createUserFormValid: false,
      creatingUser: false,
      newUser: {
        username: '',
        password: '',
        email: '',
        newTenantId: ''
      },
      selectedTenantForUser: null,
      availableTenants: [],
      loadingTenants: false,
      showPassword: false,
      // Delete User tab
      userToDelete: '',
      showDeleteConfirmation: false,
      // Update Integration Type tab
      selectedTenantForIntegration: null,
      selectedIntegration: null,
      integrations: [],
      loadingIntegrations: false,
      loadingTenantIntegration: false
    };
  },
  computed: {
    ...mapGetters(['getUser', 'getTenant', 'getTenants', 'getIntegrations']),
    emailRegex() {
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    },
    passwordRules() {
      return [
        v => !!v || this.$t('createUser.errors.passwordRequired'),
        v => (v && v.length >= 8) || 'Password must be at least 8 characters',
        v => {
          if (!v) return true;
          // Prevent XSS attack characters
          const forbiddenChars = /<|>|&|'|"|`|\\\\|;/;
          if (forbiddenChars.test(v)) {
            return 'Password contains invalid characters';
          }
          return true;
        }
      ];
    }
  },
  async mounted() {
    // Check if user is admin
    if (!this.getUser || this.getUser.name !== 'admin') {
      this.$router.push('/');
      return;
    }
    await this.loadUsers();
    await this.loadTenants();
    await this.loadIntegrations();
  },
  methods: {
    ...mapActions(['updateEmail', 'createUser', 'fetchUsers', 'deleteUser', 'fetchTenants', 'fetchIntegrations', 'updateIntegrationName', 'selectTenant', 'fetchTenantIntegration']),
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const result = await this.fetchUsers();
        if (result.success) {
          this.users = result.data;
        } else {
          this.$toast.error(result.error || "Failed to load users");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error("Failed to load users");
      } finally {
        this.loadingUsers = false;
      }
    },
    async loadTenants() {
      this.loadingTenants = true;
      try {
        const tenants = await this.fetchTenants();
        this.availableTenants = tenants || [];
      } catch (error) {
        console.error("Error fetching tenants:", error);
        this.$toast.error("Failed to load tenants");
      } finally {
        this.loadingTenants = false;
      }
    },
    getTenantDisplayName(tenant) {
      if (typeof tenant === 'string') return tenant;
      return tenant?.name || tenant?.tenantName || tenant?.id || tenant?.tenantId || 'Unknown Tenant';
    },
    getTenantValue(tenant) {
      if (typeof tenant === 'string') return tenant;
      return tenant?.id || tenant?.tenantId || tenant;
    },
    onNewTenantIdInput(value) {
      if (value) {
        this.selectedTenantForUser = null;
      }
    },
    onUserSelected(username) {
      this.selectedUser = username;
      // Find user and set their current email
      const user = this.users.find(u => u.username === username);
      if (user && user.email) {
        this.emailAddress = user.email;
        this.validateEmail();
      } else {
        this.emailAddress = '';
        this.emailError = '';
        this.emailValid = false;
      }
    },
    validateEmail() {
      if (this.emailAddress && !this.emailRegex.test(this.emailAddress)) {
        this.emailError = this.$t('email.invalid');
        this.emailValid = false;
      } else {
        this.emailError = '';
        this.emailValid = this.emailAddress && this.emailRegex.test(this.emailAddress);
      }
    },
    async handleUpdateEmail() {
      if (!this.emailValid || !this.selectedUser) return;
      
      const updateData = {
        email: this.emailAddress,
        targetUsername: this.selectedUser
      };
      
      const result = await this.updateEmail(updateData);
      if (result.success) {
        this.$toast.success(this.$t('email.updateSuccess'));
        // Reload users to get updated data
        await this.loadUsers();
      } else {
        this.$toast.error(result.error || this.$t('email.updateError'));
      }
    },
    async handleCreateUser() {
      if (!this.$refs.createUserForm.validate()) return;
      
      const tenantId = this.newUser.newTenantId || this.getTenantValue(this.selectedTenantForUser);
      
      if (!tenantId) {
        this.$toast.error(this.$t('createUser.errors.tenantRequired'));
        return;
      }
      
      this.creatingUser = true;
      try {
        const userData = {
          username: this.newUser.username,
          password: this.newUser.password,
          email: this.newUser.email,
          tenantId: tenantId
        };
        
        const result = await this.createUser(userData);
        if (result.success) {
          this.$toast.success(this.$t('createUser.createSuccess'));
          
          // Reset form
          this.newUser = {
            username: '',
            password: '',
            email: '',
            newTenantId: ''
          };
          this.selectedTenantForUser = null;
          this.showPassword = false;
          this.$refs.createUserForm.reset();
          
          await this.loadUsers();
          await this.loadTenants();
        } else {
          this.$toast.error(result.error || this.$t('createUser.createError'));
        }
      } catch (error) {
        this.$toast.error(this.$t('createUser.createError'));
      } finally {
        this.creatingUser = false;
      }
    },
    async handleDeleteUser() {
      if (!this.userToDelete) return;
      
      this.showDeleteConfirmation = false;
      const result = await this.deleteUser(this.userToDelete);
      
      if (result.success) {
        this.$toast.success(this.$t('deleteUser.deleteSuccess'));
        this.userToDelete = '';
        // Reload users
        await this.loadUsers();
      } else {
        this.$toast.error(result.error || this.$t('deleteUser.deleteError'));
      }
    },
    async loadIntegrations() {
      this.loadingIntegrations = true;
      try {
        const integrations = await this.fetchIntegrations();
        this.integrations = [{ name: 'No Integration' }, ...(integrations || [])];
      } catch (error) {
        console.error("Error fetching integrations:", error);
        this.$toast.error("Failed to load integrations");
      } finally {
        this.loadingIntegrations = false;
      }
    },
    async handleUpdateIntegrationName() {
      if (!this.selectedTenantForIntegration || !this.selectedIntegration) return;
      
      try {
        const integrationName = this.selectedIntegration === 'No Integration' ? 'none' : this.selectedIntegration;
        const result = await this.updateIntegrationName({
          tenantId: this.getTenantValue(this.selectedTenantForIntegration),
          integrationName: integrationName
        });
        
        if (result.success) {
          this.$toast.success('Integration updated successfully');
          
          const tenantId = this.getTenantValue(this.selectedTenantForIntegration);
          if (tenantId === this.getTenant) {
            await this.selectTenant(tenantId);
          }
          
          this.selectedTenantForIntegration = null;
          this.selectedIntegration = null;
        } else {
          this.$toast.error(result.error || 'Failed to update integration name');
        }
      } catch (error) {
        console.error("Error updating integration name:", error);
        this.$toast.error("Failed to update integration name");
      }
    },
    async onTenantSelectedForIntegration(tenantId) {
      if (!tenantId) {
        this.selectedIntegration = null;
        return;
      }
      
      this.loadingTenantIntegration = true;
      this.selectedIntegration = null;
      
      try {
        const result = await this.fetchTenantIntegration(this.getTenantValue(tenantId));
        if (result.success && result.data && result.data.name) {
          this.selectedIntegration = result.data.name;
        } else if (result.success && (!result.data || !result.data.name)) {
          this.selectedIntegration = 'No Integration';
        }
      } catch (error) {
        console.error("Error fetching tenant integration:", error);
        this.$toast.error("Failed to load tenant integration");
      } finally {
        this.loadingTenantIntegration = false;
      }
    }
  }
};
</script>